import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Circle, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

// Country codes list
const countryCodes = [
  { code: "+91", country: "India", flag: "🇮🇳" },
  { code: "+1", country: "USA/Canada", flag: "🇺🇸" },
  { code: "+44", country: "United Kingdom", flag: "🇬🇧" },
  { code: "+61", country: "Australia", flag: "🇦🇺" },
  { code: "+971", country: "UAE", flag: "🇦🇪" },
  { code: "+65", country: "Singapore", flag: "🇸🇬" },
  { code: "+86", country: "China", flag: "🇨🇳" },
  { code: "+81", country: "Japan", flag: "🇯🇵" },
  { code: "+82", country: "South Korea", flag: "🇰🇷" },
  { code: "+966", country: "Saudi Arabia", flag: "🇸🇦" },
  { code: "+974", country: "Qatar", flag: "🇶🇦" },
  { code: "+965", country: "Kuwait", flag: "🇰🇼" },
  { code: "+973", country: "Bahrain", flag: "🇧🇭" },
  { code: "+968", country: "Oman", flag: "🇴🇲" },
  { code: "+62", country: "Indonesia", flag: "🇮🇩" },
  { code: "+60", country: "Malaysia", flag: "🇲🇾" },
  { code: "+66", country: "Thailand", flag: "🇹🇭" },
  { code: "+92", country: "Pakistan", flag: "🇵🇰" },
  { code: "+880", country: "Bangladesh", flag: "🇧🇩" },
];

const ForgotPassword = () => {
  const [countryCode, setCountryCode] = useState("+91");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [formattedPhone, setFormattedPhone] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();

  const selectedCountry = countryCodes.find(c => c.code === countryCode) || countryCodes[0];

  // Cooldown timer effect
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const validatePhoneNumber = (phone: string) => {
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 6 && digitsOnly.length <= 15;
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePhoneNumber(phoneNumber)) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid phone number (6-15 digits)",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    const digitsOnly = phoneNumber.replace(/\D/g, '');
    const phone = countryCode + digitsOnly;
    setFormattedPhone(phone);

    try {
      const response = await supabase.functions.invoke('forgot-password', {
        body: { phoneNumber: phone },
      });

      // Handle non-2xx + 2xx-with-error uniformly
      if (response.error || response.data?.error) {
        let serverMessage = response.data?.error ?? "";

        // For non-2xx, Supabase wraps the HTTP response in error.context
        if (!serverMessage && response.error?.context) {
          try {
            const errorBody = await response.error.context.json();
            serverMessage = errorBody?.error || errorBody?.message || "";
          } catch {
            // ignore parse errors
          }
        }

        const isSpecific = Boolean(serverMessage);

        toast({
          title: isSpecific ? "Error" : "Something Went Wrong",
          description: serverMessage || "Unable to send OTP. Please check your connection and try again.",
          variant: "destructive",
        });
        return;
      }

      setOtpSent(true);
      setCooldown(60);

      toast({
        title: "OTP Sent",
        description: "A 6-digit OTP has been sent to your phone number",
      });
    } catch (error: any) {
      console.error('Forgot password error:', error);
      toast({
        title: "Something Went Wrong",
        description: "Unable to send OTP. Please check your connection and try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (otp.length !== 6) {
      toast({
        title: "Invalid OTP",
        description: "Please enter the 6-digit OTP",
        variant: "destructive",
      });
      return;
    }

    setVerifying(true);

    try {
      // Verify OTP by calling a verify endpoint
      const response = await supabase.functions.invoke('verify-reset-otp', {
        body: { phoneNumber: formattedPhone, otp },
      });

      // Handle non-2xx + 2xx-with-error uniformly
      if (response.error || response.data?.error) {
        let serverMessage = response.data?.error ?? "";

        // For non-2xx, Supabase wraps the HTTP response in error.context
        if (!serverMessage && response.error?.context) {
          try {
            const errorBody = await response.error.context.json();
            serverMessage = errorBody?.error || errorBody?.message || "";
          } catch {
            // ignore parse errors
          }
        }

        const isSpecific = Boolean(serverMessage);

        toast({
          title: isSpecific ? "Verification Failed" : "Something Went Wrong",
          description: serverMessage || "Unable to verify OTP. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Store verified state in session storage
      sessionStorage.setItem("resetPhoneNumber", formattedPhone);
      sessionStorage.setItem("resetOtpVerified", "true");
      sessionStorage.setItem("resetOtp", otp);

      toast({
        title: "OTP Verified",
        description: "Please set your new password",
      });

      navigate("/reset-password");
    } catch (error: any) {
      console.error('OTP verification error:', error);
      toast({
        title: "Something Went Wrong",
        description: "Unable to verify OTP. Please try again.",
        variant: "destructive",
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    if (cooldown > 0) return;
    
    setLoading(true);

    try {
      const response = await supabase.functions.invoke('forgot-password', {
        body: { phoneNumber: formattedPhone },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to resend OTP');
      }

      setCooldown(60);
      setOtp("");

      toast({
        title: "OTP Resent",
        description: "A new 6-digit OTP has been sent to your phone number",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to resend OTP",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-center gap-2">
          <Circle className="w-6 h-6 text-primary fill-primary" />
          <span className="text-lg font-semibold">SurgyDischarge</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex items-center justify-center px-4 pt-20">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">Forgot Password</h1>
            <p className="text-sm text-muted-foreground">
              {otpSent 
                ? `Enter the 6-digit OTP sent to ${formattedPhone}`
                : "Enter your phone number to receive a password reset OTP"}
            </p>
          </div>

          {!otpSent ? (
            // Phone number input form
            <form onSubmit={handleSendOTP} className="space-y-6">
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger className="w-[130px] bg-background">
                      <SelectValue>
                        <span className="flex items-center gap-2">
                          <span className="text-lg">{selectedCountry.flag}</span>
                          <span className="text-sm font-medium">{selectedCountry.code}</span>
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] bg-background z-50">
                      {countryCodes.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          <span className="flex items-center gap-2">
                            <span className="text-lg">{country.flag}</span>
                            <span className="font-medium">{country.code}</span>
                            <span className="text-muted-foreground text-xs">{country.country}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Enter Mobile number"
                    value={phoneNumber}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      setPhoneNumber(digits);
                    }}
                    disabled={loading}
                    className="flex-1 text-base"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold bg-[hsl(207,100%,45%)] hover:bg-[hsl(207,100%,40%)] text-white shadow-lg transition-all duration-150 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-md" 
                disabled={loading}
              >
                {loading ? "Sending..." : "Send OTP"}
              </Button>

              <div className="text-center">
                <Link 
                  to="/login" 
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Login
                </Link>
              </div>
            </form>
          ) : (
            // OTP verification form
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div className="space-y-4">
                <div className="flex justify-center">
                  <InputOTP
                    value={otp}
                    onChange={setOtp}
                    maxLength={6}
                    disabled={verifying}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                {/* Resend OTP */}
                <div className="text-center text-sm text-muted-foreground">
                  Didn't receive OTP?{" "}
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={loading || cooldown > 0}
                    className="text-primary font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Sending..." : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend Now"}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold bg-[hsl(207,100%,45%)] hover:bg-[hsl(207,100%,40%)] text-white shadow-lg transition-all duration-150 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-md" 
                disabled={verifying || otp.length !== 6}
              >
                {verifying ? "Verifying..." : "Verify OTP"}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setOtpSent(false);
                    setOtp("");
                  }}
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Change Phone Number
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
